import { useState } from 'react';

import { type IdentityProvider } from '../../../shared/types';
import { useInvertedLocalize } from '../../../shared/utils/localization';
import { type ProviderEditProps, Providers, type SamlProvider } from '../types';
import { type ValidationResult } from '../types/provider-types';

import { useSamlEditView } from './useSamlEditView';

export const MAX_ALIAS_LENGTH = 32;
export const MAX_DISPLAY_NAME_LENGTH = 16;

interface ProviderHooks {
  activeProvider: Providers | null;
  canSave: boolean;
  isSaving: boolean;
  isNew: boolean;
  error: string;
  saveProvider: () => void;
  cancelProviderEdit: () => void;
  newProvider: (type: Providers) => void;
  editProvider: (provider: IdentityProvider) => void;

  saml: ProviderEditProps<SamlProvider>;
}

function getProviderType(provider: IdentityProvider): Providers | null {
  switch (provider.protocol) {
    case 'saml':
      return Providers.SAML;
    default:
      return null;
  }
}

/** Hook to allow editing all providers regardless of type */
export function useProviderEdit(
  providers: IdentityProvider[],
  reloadProviders: () => void
): ProviderHooks {
  const l = useInvertedLocalize();
  const [activeProvider, setEditProvider] = useState<Providers | null>(null);

  const samlEdit = useSamlEditView();

  const actions: Record<
    Providers,
    {
      onNew: () => void;
      onSave: () => Promise<void>;
      loadProvider: (alias: string) => void;
      canSave: boolean;
      isSaving: boolean;
      isNew: boolean;
      error: string;
    }
  > = {
    SAML: {
      onNew: () => samlEdit.newProvider(),
      onSave: () => samlEdit.saveProvider(),
      loadProvider: (alias: string) => samlEdit.loadProvider(alias),
      canSave: samlEdit.canSave,
      isSaving: samlEdit.isSaving,
      isNew: samlEdit.isNew,
      error: samlEdit.error,
    },
  };

  const canSave = activeProvider ? actions[activeProvider].canSave : false;
  const isSaving = activeProvider ? actions[activeProvider].isSaving : false;
  const isNew = activeProvider ? actions[activeProvider].isNew : false;
  const error = activeProvider ? actions[activeProvider].error : '';

  const saveProvider = () => {
    if (activeProvider) {
      actions[activeProvider]
        .onSave()
        .then(() => {
          reloadProviders();
        })
        .catch(() => {
          // Nothing to do here, error will be bubbled up.
        });
    }
  };

  const cancelProviderEdit = () => {
    setEditProvider(null);
  };

  const newProvider = (provider: Providers) => {
    setEditProvider(provider);
    actions[provider].onNew();
  };

  const editProvider = (provider: IdentityProvider) => {
    const providerType = getProviderType(provider);

    if (!providerType) {
      return;
    }

    setEditProvider(providerType);
    actions[providerType].loadProvider(provider.alias);
  };

  const validateAlias = (provider: IdentityProvider): ValidationResult => {
    const value = provider.alias;
    const found = providers.filter((p) => p.alias === value && isNew);

    if (found.length) {
      return { isValid: false, message: l('Alias already exists') };
    }

    const isValid =
      /^[a-z0-9]*$/.test(provider.alias) &&
      provider.alias.length > 0 &&
      provider.alias.length <= MAX_ALIAS_LENGTH;

    return {
      isValid,
      message: isValid
        ? ''
        : l('Up to {count} lower-case alphaneumeric characters are allowed.', {
            count: MAX_ALIAS_LENGTH,
          }),
    };
  };

  const validateName = (provider: IdentityProvider): ValidationResult => {
    const name = provider.name.trim();

    const found = providers.filter(
      (p) => p.name === name && (isNew || p.alias !== provider.alias)
    );

    if (found.length) {
      return { isValid: false, message: l('Display Name already exists') };
    }

    const isValid =
      /^[a-zA-Z0-9 ]*$/.test(name) &&
      name.length > 0 &&
      name.length <= MAX_DISPLAY_NAME_LENGTH;

    return {
      isValid,
      message: isValid
        ? ''
        : l('Up to {count} alphaneumeric and space characters are allowed.', {
            count: MAX_DISPLAY_NAME_LENGTH,
          }),
    };
  };

  return {
    activeProvider,
    canSave,
    isSaving,
    isNew,
    error,
    saveProvider,
    cancelProviderEdit,
    newProvider,
    editProvider,

    saml: {
      provider: samlEdit.provider,
      validateName,
      validateAlias,
      setProvider: samlEdit.setProvider,
      setCanSave: samlEdit.setCanSave,
      isNew: samlEdit.isNew,
      isLoading: samlEdit.isLoading,
      error: samlEdit.error,
    },
  };
}
